// @ts-nocheck
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Plus, Truck, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/DataTable';
import AppContext from '../../contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';
import { DeploymentCreateDialog } from './components/DeploymentCreateDialog';
import { DeploymentDeleteDialog } from './components/DeploymentDeleteDialog';
import { DeploymentStatusBadge, CampaignTypeBadge, campaignTypeLabel } from './components/StatusBadges';
import { useDeployments } from '../../hooks/useDeployments';
import { useDeleteDeployment } from '../../hooks/useDeleteDeployment';
import type { Deployment, CampaignType } from '../../api/deploymentsApi';

const AdminDeployments = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext) as any;
  const canManage = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_DEPLOYMENTS_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [deploymentToDelete, setDeploymentToDelete] = useState<Deployment | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<CampaignType | null>(null);
  const { deployments, isLoading, error } = useDeployments();

  // Tabs are built from the campaign types actually present in the list.
  const presentTypes = useMemo(
    () => Array.from(new Set((deployments ?? []).map((d) => d.campaign_type))) as CampaignType[],
    [deployments]
  );
  const filteredDeployments = useMemo(
    () => (typeFilter ? (deployments ?? []).filter((d) => d.campaign_type === typeFilter) : deployments),
    [deployments, typeFilter]
  );
  const { mutate: deleteDeployment, isPending: deleting } = useDeleteDeployment({
    onSuccess: () => {
      setDeploymentToDelete(null);
      setDeleteError(null);
    },
    onError: (err) => setDeleteError(err.message),
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('Name'),
        cell: ({ row }: any) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'campaign_type',
        header: t('Type'),
        cell: ({ row }: any) => <CampaignTypeBadge type={row.original.campaign_type} />,
      },
      {
        accessorKey: 'service_desk_id',
        header: t('Service Desk'),
        cell: ({ row }: any) => `SD ${row.original.service_desk_id}`,
      },
      {
        accessorKey: 'status',
        header: t('Status'),
        cell: ({ row }: any) => <DeploymentStatusBadge status={row.original.status} />,
      },
      {
        id: 'dates',
        header: t('Dates'),
        cell: ({ row }: any) => {
          const d = row.original;
          return (
            <span className="text-sm text-gray-600">
              {dayjs(d.start_date).format('DD/MM/YYYY')}
              {d.end_date ? ` → ${dayjs(d.end_date).format('DD/MM/YYYY')}` : ''}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: t('Created'),
        cell: ({ row }: any) => dayjs(row.original.created_at).format('DD/MM/YYYY'),
      },
      {
        id: 'actions',
        header: t('Action'),
        enableSorting: false,
        cell: ({ row }: any) => (
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/admin/deployments/${row.original.id}`)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title={t('Open')}
            >
              <Edit className="w-4 h-4" />
            </button>
            {canManage && (
              <button
                onClick={() => {
                  setDeleteError(null);
                  setDeploymentToDelete(row.original);
                }}
                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                title={t('Delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate, canManage, t]
  );

  return (
    <div className="container mx-auto p-6 space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Truck className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>{t('Deployments')}</CardTitle>
                <p className="text-sm text-gray-500">{t('Manage bulk deployments / remerchandising')}</p>
              </div>
            </div>
            {canManage && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('New campaign')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : error ? (
            <p className="text-sm text-red-600">{error.message}</p>
          ) : (
            <>
              {presentTypes.length > 1 && (
                <div className="mb-4 flex gap-2">
                  <button
                    onClick={() => setTypeFilter(null)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      typeFilter === null ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('All (masculine)')}
                  </button>
                  {presentTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        typeFilter === type ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {campaignTypeLabel(t, type)}
                    </button>
                  ))}
                </div>
              )}
              <DataTable
                data={filteredDeployments}
                columns={columns}
                emptyMessage={t('No campaign')}
                pageSize={20}
              />
            </>
          )}
        </CardContent>
      </Card>

      <DeploymentCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          navigate(`/admin/deployments/${id}`);
        }}
      />

      <DeploymentDeleteDialog
        deployment={deploymentToDelete}
        open={!!deploymentToDelete}
        loading={deleting}
        error={deleteError}
        onConfirm={() => deploymentToDelete && deleteDeployment(deploymentToDelete.id)}
        onClose={() => {
          setDeploymentToDelete(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
};

export default AdminDeployments;
